import React, { useEffect, useState, useCallback, forwardRef, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import Detail from "./Detail";
import ConfirmDialog from "../dialogs/ConfirmDialog";
import eventEmitter from "../utils/eventEmitter";
import {
  getPrimaryField as getPrimaryFieldHelper,
  buildSaveDiscardText as buildSaveDiscardTextHelper,
} from "../utils/helper";
import ValidationProvider, { useForm } from "../utils/ValidationProvider";
import { useBlockerRegistration } from "../utils/BlockerProvider";

let _lastAction = null;

/**
 * Detail wrapper component
 * @module
 * @param {func} onSubmit - On form submit function
 * @param {detailSchema[]} schema - Detail Schema
 * @param {object} data - Data
 * @param {func} onChange - On field change function
 * @param {func} onDiscard - On discard changes function
 * @param {func} onBlur - On field blur function
 * @param {object} parentProps - Parent props
 * @param {bool} disabled - Read only detail
 * @param {number} akcDisabled - Disabled actions mask number
 * @param {number} akcHidden - Hidden actions mask number
 * @param {bool} isLoading - Is loading
 * @param {array} ui - Custom schema
 * @param {object} args - Custom arguments
 * @param {element} toolbarActions - Toolbar actions
 * @param {string} primaryField - Primary field
 * @param {func} onAdd - On entry add function
 * @param {func} onCreate - On entry create function
 * @param {func} onUpdate - On entry update function
 * @param {func} onUpload - On file upload function
 * @param {bool} toolbarHidden - Toolbar hidden
 * @param {func} onGet - On data get function
 * @param {object} allData - All data
 * @param {element} currentEl - Current element
 * @param {func} next - Switches to the next entry function
 * @param {func} previous - Switches to the previous entry function
 * @param {bool} modified - Is current entry modified
 * @param {string} discardText - Discard text
 * @param {string} saveChangesDialogText - SaveChangesDialog text
 * @param {bool} isPicker - Is field picker
 * @param {bool} hiddenBack - Hidden back arrow in detail toolbar
 * @param {string} title - Detail title
 * @param {array} pages - Pages
 * @param {func} componentFactory - Factory for custom components
 * @param {bool} hiddenSave - Hidden save button in detail toolbar
 * @param {bool} sidebarHorizontal - Should sidebar be horizontal?
 * @param {string} [db] - db
 */

const DetailWrapper = forwardRef((props, ref) => {
  const formValidation = useForm(props.customValidation, props.data);
  const [isOpenDiscard, setIsOpenDiscard] = useState(false);
  const [isOpenSaveChangesDialog, setIsOpenSaveChangesDialog] = useState(false);
  const [id, setId] = useState(0);
  const [dataInvalid, setDataInvalid] = useState(false);
  const [nextLocation, setNextLocation] = useState(null);
  const [resetValue, setResetValue] = useState(false);
  const { onChange, onBlur } = props;

  const inEdit = props.parentProps && props.parentProps[props.primaryField];

  const blockerCondition = useCallback(() => props.modified && !nextLocation, [props.modified, nextLocation])
  const blockerFunction = useCallback((newLocation) => {
    setNextLocation(newLocation)
    _lastAction = "exit";
    if (inEdit) {
      handleOpenSaveChangesDialog();
    } else {
      handleOpenDiscard();
    }
  }, [inEdit])
  const proceedBlocker = useBlockerRegistration(blockerCondition, blockerFunction)

  //create ref with funcions
  useImperativeHandle(ref, () => ({
    getValue: (field) => getValue(field),
    removeInvalidClass: (field) => removeInvalidClass(field),
    handleBlur: (field, value) => handleBlur(field, value !== null ? value : getValue(field.field)),
    handleSubmit: (event) => handleSubmit(event),
    validateFields: async () => { return await formValidation.validate() },
    isInvalid: async (filed, value) => { return await formValidation.validateField(filed, value) },
    addInvalidClass: (filed) => formValidation.addInvalid(filed),
    invalidRecommended: formValidation.invalidRecommended,
    props,
    ref
  }));

  const getPrimaryField = useCallback(() => {
    return getPrimaryFieldHelper(props.primaryField);
  }, [props.primaryField]);

  const removeInvalidClass = useCallback((field) => {
    let doc = document;
    if (props.parentClassName) {
      doc = document.getElementsByClassName(props.parentClassName)[0];
    }
    let element = doc.getElementsByClassName(`form-field-${field.field}`)[0];
    if (element) {
      removeFieldClass(element);
    }
  }, [props.parentClassName]);

  const removeInvalidClasses = useCallback(() => {
    formValidation.cleanValidationState();
  }, [formValidation]);

  useEffect(() => {
    formValidation.cleanValidationState();
    props.schema.map(x => {
      if (x.hidden) {
        formValidation.registerValidationInput(x, () => { }, true);
      }
    });
  }, [props.schema]);

  useEffect(() => {
    if (props.data && props.data[getPrimaryField()] !== id) {
      removeInvalidClasses();
      setId(props.data[getPrimaryField()]);
      setResetValue(false);
    }
  }, [props.data, getPrimaryField, id, removeInvalidClasses]);

  const handleOpenDiscard = () => {
    setIsOpenDiscard(true);
  };

  const handleCloseDiscard = () => {
    setIsOpenDiscard(false);
    setTimeout(() => {
      setNextLocation(null);
    }, 0)
  };

  const handleConfirmDiscard = () => {
    switch (_lastAction) {
      case "next":
        next();
        break;
      case "previous":
        previous();
        break;
      case "back":
        back();
        break;
      case "exit":
        processExit()
        break;
      default:
        break;
    }

    handleCloseDiscard();
  };

  const handleOpenSaveChangesDialog = () => {
    setIsOpenSaveChangesDialog(true);
  };

  const handleCloseSaveChangesDialog = () => {
    setIsOpenSaveChangesDialog(false);
    setTimeout(() => {
      setNextLocation(null);
    }, 0)
  };

  const handleCancelSaveChangesDialog = () => {
    switch (_lastAction) {
      case "next":
        next();
        break;
      case "previous":
        previous();
        break;
      case "back":
        back();
        break;
      case "exit":
        processExit();
        break;
      default:
        break;
    }

    handleCloseSaveChangesDialog();
  };

  const handleConfirmSaveChangesDialog = async () => {
    await handleSubmit(new Event("submit"));

    handleCancelSaveChangesDialog();
  };

  const onSubmit = async () => {
    const {
      data,
      primaryField,
      onUpdate,
      onCreate,
      onSubmit: onSubmitProps,
    } = props;

    const id = data[getPrimaryFieldHelper(primaryField)];
    if (id) {
      await onUpdate(id, data);
    } else {
      await onCreate(data);
    }

    // TODO: OVERIT
    onSubmitProps();
  };

  const tryNext = () => {
    if (props.modified) {
      _lastAction = "next";
      if (inEdit) {
        handleOpenSaveChangesDialog();
      } else {
        handleOpenDiscard();
      }
      return;
    }

    next();
  };

  const tryPrevious = () => {
    if (props.modified) {
      _lastAction = "previous";
      if (inEdit) {
        handleOpenSaveChangesDialog();
      } else {
        handleOpenDiscard();
      }
      return;
    }

    previous();
  };

  const back = () => {
    eventEmitter.changeTab(props.primaryField, 0);

    cleanDetail();
  };

  const previous = () => {
    if (props.prev) {
      props.prev();
    }
    let ID = props.data[getPrimaryField()];
    let minID = props.allData[0][getPrimaryField()];
    let index = props.allData.findIndex(
      (item) => item[getPrimaryField()] === ID
    );
    if (ID !== minID) {
      const previousItem = props.allData[index - 1];
      eventEmitter.changeSelected(
        [previousItem],
        getPrimaryField()
      );
      removeInvalidClasses();
    }
  };

  const next = () => {
    if (props.next) {
      props.next();
    }
    let ID = props.data[getPrimaryField()];
    let maxID = props.allData[props.allData.length - 1][getPrimaryField()];
    let index = props.allData.findIndex(
      (item) => item[getPrimaryField()] === ID
    );
    if (ID !== maxID) {
      const nextItem = props.allData[index + 1];
      eventEmitter.changeSelected(
        [nextItem],
        getPrimaryField()
      );
      removeInvalidClasses();
    }
  };

  const disabledNext = () => {
    let ID = props.data ? props.data[getPrimaryField()] : null;
    let data = props.allData ? props.allData : [];
    let index = data?.findIndex((item) => item[getPrimaryField()] === ID);
    if (index < data.length - 1) {
      return false;
    } else {
      return true;
    }
  };

  const handleBack = (event) => {
    if (event === undefined && props.modified) {
      _lastAction = "back";
      if (inEdit) {
        handleOpenSaveChangesDialog();
      } else {
        handleOpenDiscard();
      }
      return;
    }

    eventEmitter.changeTab(props.primaryField, 0);
  };

  const disabledPrevious = () => {
    let ID = props.data ? props.data[getPrimaryField()] : null;
    let data = props.allData ? props.allData : [];
    let index = data.findIndex((item) => item[getPrimaryField()] === ID);
    if (index > 0) {
      return false;
    } else {
      return true;
    }
  };

  const prepareSubmit = async () => {
    if (props.parentProps && props.parentProps.isPicker) {
      await onSubmit();
      props.parentProps.applyFilter(props.data[getPrimaryField()]);
    } else {
      await onSubmit();
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const areFieldsInvalid = await formValidation.validate();

    if (!areFieldsInvalid) {
      await prepareSubmit();
      return true;
    } else {
      setDataInvalid(true);
      setTimeout(() => {
        setDataInvalid(false);
      }, 4500);
    }
    return false;
  };

  const removeFieldClass = (element) => {
    element.classList.remove("validation-valid");
    element.classList.remove("recommended-invalid");
    element.classList.remove("validation-invalid");
  };

  const getValue = useCallback((field) => {
    return _.get(props.data, field);
  }, [props.data])

  const handleBlur = useCallback(async (field, value) => {
    if (onBlur) {
      onBlur(field.field, value);
    }
  }, [onBlur])

  const buildText = (id) => {
    const saveChanges = inEdit;
    return buildSaveDiscardTextHelper(
      id,
      saveChanges,
      props.saveChangesDialogText,
      props.discardText
    );
  };

  const buildRecommendedText = () => {
    const fields = formValidation.invalidRecommended.map((el) => el.title);
    return `Máte nevyplnené odporúčané ${fields.length === 1 ? "pole" : "polia"
      }: ${fields.toString()}. Chcete uložiť zmeny?`;
  };

  const handleConfirmRecommended = () => {
    prepareSubmit();
    handleCloseRecommended();
  };

  const handleCloseRecommended = () => {
    formValidation.setInvalidRecommended([]);
  };

  const cleanDetail = () => {
    const ID = props.data[getPrimaryField()];
    if (ID) {
      eventEmitter.changeSelected(
        [props.allData.find(e => e[getPrimaryField()] === ID)],
        getPrimaryField()
      );
    }

    if (props.onDiscard) {
      props.onDiscard();
    }

    removeInvalidClasses();
  }

  const processExit = () => {
    proceedBlocker()
  }

  const handleChange = useCallback(async (name, value) => {
    if (onChange) {
      setResetValue(true);
      await onChange(name, value);      
    }
  }, [onChange])

  const ids = props.data ? props.data[getPrimaryField()] : null;
  return (
    <React.Fragment>
      <ValidationProvider {...formValidation}>
        <Detail
          ref={ref}
          handleSubmit={handleSubmit}
          schema={props.schema}
          data={props.data}
          onAdd={props.onAdd}
          onGet={props.onGet}
          getValue={getValue}
          onChange={handleChange}
          handleBlur={handleBlur}
          onUpload={props.onUpload}
          parentProps={{ ...props.parentProps, resetValue: resetValue }}
          frms={props.frms}
          disabled={props.disabled}
          akc={props.akc}
          akcDisabled={props.akcDisabled}
          akcHidden={props.akcHidden}
          isLoading={props.isLoading}
          ui={props.ui}
          args={props.args}
          toolbarActions={props.toolbarActions}
          primaryField={props.primaryField}
          toolbarHidden={props.toolbarHidden}
          previous={tryPrevious}
          next={tryNext}
          disabledNext={disabledNext()}
          disabledPrevious={disabledPrevious()}
          currentEl={props.currentEl}
          pageTitle={props.pageTitle}
          onBack={handleBack}
          isPicker={props.isPicker}
          hiddenBack={props.hiddenBack}
          title={props.title}
          pages={props.pages}
          hiddenSave={props.hiddenSave}
          hiddenPrint={props.hiddenPrint}
          dataInvalid={dataInvalid}
          sidebarHorizontal={props.sidebarHorizontal}
          children={props.children}
          withMeta={props.withMeta}
          db={props.db}
          componentFactory={props.componentFactory}
          akcFactory={props.akcFactory}
          showAbort={props.showAbort}
          reducer={props.reducer}
          allowMultiple={props.allowMultiple}
          openedSidepanels={props.openedSidepanels}
        />
        {isOpenDiscard && (
          <ConfirmDialog
            isOpen={isOpenDiscard}
            onClose={handleCloseDiscard}
            onConfirm={handleConfirmDiscard}
            text={buildText(ids)}
            confirmText="Áno"
            cancelText="Nie"
          />
        )}
        {isOpenSaveChangesDialog && (
          <ConfirmDialog
            isOpen={isOpenSaveChangesDialog}
            onClose={handleCloseSaveChangesDialog}
            onCancel={handleCancelSaveChangesDialog}
            onConfirm={handleConfirmSaveChangesDialog}
            text={buildText(ids)}
            confirmText="Uložiť"
            cancelText="Zahodiť zmeny"
          />
        )}
        <ConfirmDialog
          isOpen={formValidation.invalidRecommended.length !== 0}
          onClose={handleCloseRecommended}
          onConfirm={handleConfirmRecommended}
          text={buildRecommendedText()}
          confirmText="Uložiť"
        />
      </ValidationProvider>
    </React.Fragment>
  );
});

DetailWrapper.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  schema: PropTypes.array,
  data: PropTypes.object,
  onChange: PropTypes.func,
  onDiscard: PropTypes.func,
  onBlur: PropTypes.func,
  parentProps: PropTypes.object,
  frms: PropTypes.array,
  disabled: PropTypes.bool,
  akcDisabled: PropTypes.number,
  akcHidden: PropTypes.number,
  isLoading: PropTypes.bool.isRequired,
  ui: PropTypes.array,
  args: PropTypes.object,
  toolbarActions: PropTypes.any,
  primaryField: PropTypes.string.isRequired,
  onAdd: PropTypes.func,
  onCreate: PropTypes.func,
  onUpdate: PropTypes.func.isRequired,
  onUpload: PropTypes.func,
  toolbarHidden: PropTypes.bool,
  onGet: PropTypes.func.isRequired,
  allData: PropTypes.array.isRequired,
  currentEl: PropTypes.element,
  next: PropTypes.func,
  prev: PropTypes.func,
  modified: PropTypes.bool,
  discardText: PropTypes.string,
  saveChangesDialogText: PropTypes.string,
  isPicker: PropTypes.bool,
  hiddenBack: PropTypes.bool,
  title: PropTypes.string,
  pages: PropTypes.arrayOf(PropTypes.object),
  hiddenSave: PropTypes.bool,
  sidebarHorizontal: PropTypes.bool,
  withMeta: PropTypes.bool,
  componentFactory: PropTypes.func,
  db: PropTypes.string
};

export default DetailWrapper;